import { ApolloLink } from "@apollo/client";
import { declaresAtLeast } from "./fence-documents";
import type { FenceContext } from "./fence-present-link";

/**
 * THE arg-era concession — and the one file the F2 header lane deletes
 * (along with the store's scope narrowing; see the SCOPE FE section).
 *
 * The fence pilot carries its token as a per-field `atLeast` argument, so
 * SOMETHING must transcribe the present link's context tokens into the
 * fenced document's variable. Fence-aware documents mark THEMSELVES —
 * declaring `$atLeast` is the opt-in (fence-documents.ts; the revision
 * package's comment-fence-contract test pins the marker on the pilot
 * document), so the match survives renames and needs no cross-package
 * name constant. That transcription is deliberately
 * quarantined here: hooks never thread tokens (the membership refetch
 * stays a bare `refetch()`), and the store and capture/present links are
 * already header-shaped.
 *
 * Token choice is scope-aware: prefer the newest live token whose scope
 * (the containers its mutation payload said it affects) includes the
 * read's `$id`, then fall back to the newest SCOPE-UNKNOWN token — one
 * whose payload named no readable containers (`revisions: null`,
 * precisely the projection-lag case the fence exists for) or names none
 * by shape (an edit). Presenting broadly is sound: the presence loop's
 * terminal verdict is entity-keyed and issue-time-ordered, so any read's
 * honest disclosure is a monotone fact about the token, and the only
 * cost of over-presentation is a bounded server-side wait. A token
 * positively scoped to OTHER containers stays withheld — its payload
 * named its threads, and this read is not one of them.
 */
export const fenceAtLeastAdapterLink = new ApolloLink((operation, forward) => {
  if (declaresAtLeast(operation.query)) {
    // Boundary assertion, safe by construction: the `fence` context slot
    // is written only by the sibling present link, which types it as
    // FenceContext at the write.
    const { fence } = operation.getContext() as { fence?: FenceContext };
    const containerId = operation.variables?.["id"];
    const callerToken = operation.variables?.["atLeast"];
    if (fence && typeof containerId === "string" && callerToken == null) {
      const match =
        fence.tokens.find(({ scopeIds }) => scopeIds.includes(containerId)) ??
        fence.tokens.find(({ scopeIds }) => scopeIds.length === 0);
      if (match) {
        operation.variables = { ...operation.variables, atLeast: match.token };
        // The capture link folds this read's convergence disclosure back
        // into the presented token — the correlation rides the context.
        operation.setContext({ fencePresentedToken: match.token });
      }
    }
  }
  return forward(operation);
});
